import { useMemo } from "react";
import { Label, Pie, PieChart } from "recharts";

import { Card, CardContent } from "@/components/ui/card";
import {
	ChartConfig,
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from "@/components/ui/chart";

interface AdminPanelSalesTotalSalesProps {
	sales_count_by_platform: {
		platform: "B4you" | "Monetizze" | "Braip";
		count: number;
	}[];
}

export function AdminPanelSalesTotalSales({
	sales_count_by_platform,
}: AdminPanelSalesTotalSalesProps) {
	const chartConfig: ChartConfig = {
		b4you: {
			label: "B4you",
			color: "hsl(var(--the-mart))",
		},
		braip: {
			label: "Braip",
			color: "hsl(var(--braip))",
		},
		monetizze: {
			label: "Monetizze",
			color: "hsl(var(--monetizze))",
		},
	};

	// Gerar o chartData a partir de sales_count_by_platform e usar o chartConfig para mapear as cores
	const chartData = sales_count_by_platform.map((entry) => ({
		platform: entry.platform,
		sales: entry.count,
		fill: chartConfig[entry.platform.toLowerCase()]?.color || "#6C7373", // Usar a cor do chartConfig ou "#6C7373" como fallback
	}));

	const totalSales = useMemo(() => {
		return chartData.reduce((acc, curr) => acc + curr.sales, 0);
	}, [chartData]);

	// Verifica se todos os dados de vendas são 0
	const isEmpty = totalSales === 0;

	return (
		<div className="flex flex-col gap-10 rounded-[8px] border border-solid border-neutral-100 p-6">
			<h2 className="font-inter text-P3 font-medium leading-140 text-neutral-1100">
				Total em vendas
			</h2>

			<div className="flex items-center gap-10">
				<div className="w-[380px]">
					<Card className="flex flex-col border-none bg-transparent">
						<CardContent>
							<ChartContainer
								config={chartConfig}
								className="aspect-square max-h-[190px]"
							>
								<PieChart>
									<ChartTooltip
										cursor={false}
										content={<ChartTooltipContent hideLabel />}
									/>
									{/* Mostrar o gráfico apenas se houver vendas */}
									{!isEmpty ? (
										<Pie
											data={chartData}
											dataKey="sales"
											nameKey="platform"
											innerRadius={60}
											strokeWidth={5}
										>
											<Label
												content={({ viewBox }) => {
													if (viewBox && "cx" in viewBox && "cy" in viewBox) {
														return (
															<text
																x={viewBox.cx}
																y={viewBox.cy}
																textAnchor="middle"
																dominantBaseline="middle"
															>
																<tspan
																	x={viewBox.cx}
																	y={viewBox.cy}
																	className="fill-neutral-1100 font-inter text-P2 font-medium leading-140"
																>
																	{totalSales.toLocaleString()}
																</tspan>
																<tspan
																	x={viewBox.cx}
																	y={(viewBox.cy || 0) + 24}
																	className="fill-neutral-500 font-inter text-P6 font-normal leading-160"
																>
																	Vendas
																</tspan>
															</text>
														);
													}
													return null;
												}}
											/>
										</Pie>
									) : (
										// Exibir o gráfico vazio com cor de fundo #6C7373 se não houver dados de vendas
										<svg
											width="80%"
											height="80%"
											viewBox="0 0 140 140"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M139.952 69.9741C139.952 108.621 108.623 139.95 69.976 139.95C31.3293 139.95 0 108.621 0 69.9741C0 31.3274 31.3293 -0.00195312 69.976 -0.00195312C108.623 -0.00195312 139.952 31.3274 139.952 69.9741ZM10.4964 69.9741C10.4964 102.824 37.1263 129.454 69.976 129.454C102.826 129.454 129.456 102.824 129.456 69.9741C129.456 37.1244 102.826 10.4944 69.976 10.4944C37.1263 10.4944 10.4964 37.1244 10.4964 69.9741Z"
												fill="#262C2C"
											/>
											<path
												d="M69.4631 67.7415C68.3409 67.7367 67.3821 67.4408 66.5866 66.8537C65.7912 66.2666 65.1828 65.4119 64.7614 64.2898C64.34 63.1676 64.1293 61.8158 64.1293 60.2344C64.1293 58.6577 64.34 57.3106 64.7614 56.1932C65.1875 55.0758 65.7983 54.2235 66.5938 53.6364C67.3939 53.0492 68.3504 52.7557 69.4631 52.7557C70.5758 52.7557 71.5298 53.0516 72.3253 53.6435C73.1207 54.2306 73.7292 55.0829 74.1506 56.2003C74.5767 57.313 74.7898 58.6577 74.7898 60.2344C74.7898 61.8205 74.5791 63.1747 74.1577 64.2969C73.7363 65.4143 73.1278 66.2689 72.3324 66.8608C71.5369 67.4479 70.5805 67.7415 69.4631 67.7415ZM69.4631 65.8452C70.4479 65.8452 71.2173 65.3646 71.7713 64.4034C72.33 63.4422 72.6094 62.0526 72.6094 60.2344C72.6094 59.027 72.4815 58.0066 72.2259 57.1733C71.9749 56.3352 71.6127 55.7008 71.1392 55.2699C70.6705 54.8343 70.1117 54.6165 69.4631 54.6165C68.483 54.6165 67.7135 55.0994 67.1548 56.0653C66.5961 57.0312 66.3144 58.4209 66.3097 60.2344C66.3097 61.4465 66.4351 62.4716 66.6861 63.3097C66.9418 64.143 67.304 64.7751 67.7727 65.206C68.2415 65.6321 68.8049 65.8452 69.4631 65.8452Z"
												fill="white"
											/>
											<path
												d="M49.9148 79.7727L52.5057 87.1193H52.608L55.1989 79.7727H56.3068L53.1023 88.5H52.0114L48.8068 79.7727H49.9148ZM59.6648 88.6364C59.0341 88.6364 58.4901 88.4972 58.0327 88.2188C57.5781 87.9375 57.2273 87.5455 56.9801 87.0426C56.7358 86.5369 56.6136 85.9489 56.6136 85.2784C56.6136 84.608 56.7358 84.017 56.9801 83.5057C57.2273 82.9915 57.571 82.5909 58.0114 82.304C58.4545 82.0142 58.9716 81.8693 59.5625 81.8693C59.9034 81.8693 60.2401 81.9261 60.5724 82.0398C60.9048 82.1534 61.2074 82.3381 61.4801 82.5938C61.7528 82.8466 61.9702 83.1818 62.1321 83.5994C62.294 84.017 62.375 84.5312 62.375 85.142V85.5682H57.3295V84.6989H61.3523C61.3523 84.3295 61.2784 84 61.1307 83.7102C60.9858 83.4205 60.7784 83.1918 60.5085 83.0241C60.2415 82.8565 59.9261 82.7727 59.5625 82.7727C59.1619 82.7727 58.8153 82.8722 58.5227 83.071C58.233 83.267 58.0099 83.5227 57.8537 83.8381C57.6974 84.1534 57.6193 84.4915 57.6193 84.8523V85.4318C57.6193 85.9261 57.7045 86.3452 57.875 86.6889C58.0483 87.0298 58.2884 87.2898 58.5952 87.4688C58.902 87.6449 59.2585 87.733 59.6648 87.733C59.929 87.733 60.1676 87.696 60.3807 87.6222C60.5966 87.5455 60.7827 87.4318 60.9389 87.2812C61.0952 87.1278 61.2159 86.9375 61.3011 86.7102L62.2727 86.983C62.1705 87.3125 61.9986 87.6023 61.7571 87.8523C61.5156 88.0994 61.2173 88.2926 60.8622 88.4318C60.5071 88.5682 60.108 88.6364 59.6648 88.6364ZM64.9105 84.5625V88.5H63.9048V81.9545H64.8764V82.9773H64.9616C65.1151 82.6449 65.348 82.3778 65.6605 82.1761C65.973 81.9716 66.3764 81.8693 66.8707 81.8693C67.3139 81.8693 67.7017 81.9602 68.0341 82.142C68.3665 82.321 68.625 82.5938 68.8097 82.9602C68.9943 83.3239 69.0866 83.7841 69.0866 84.3409V88.5H68.081V84.4091C68.081 83.8949 67.9474 83.4943 67.6804 83.2074C67.4134 82.9176 67.0469 82.7727 66.581 82.7727C66.2599 82.7727 65.973 82.8423 65.7202 82.9815C65.4702 83.1207 65.2727 83.3239 65.1278 83.5909C64.983 83.858 64.9105 84.1818 64.9105 84.5625ZM73.396 88.6364C72.8505 88.6364 72.369 88.4986 71.9513 88.223C71.5337 87.9446 71.207 87.5526 70.9712 87.0469C70.7354 86.5384 70.6175 85.9375 70.6175 85.2443C70.6175 84.5568 70.7354 83.9602 70.9712 83.4545C71.207 82.9489 71.5352 82.5582 71.9556 82.2827C72.3761 82.0071 72.8619 81.8693 73.413 81.8693C73.8391 81.8693 74.1758 81.9403 74.4229 82.0824C74.6729 82.2216 74.8633 82.3807 74.994 82.5597C75.1275 82.7358 75.2312 82.8807 75.305 82.9943H75.3903V79.7727H76.396V88.5H75.4244V87.4943H75.305C75.2312 87.6136 75.1261 87.7642 74.9897 87.946C74.8533 88.125 74.6587 88.2855 74.4059 88.4276C74.1531 88.5668 73.8164 88.6364 73.396 88.6364ZM73.5323 87.733C73.9357 87.733 74.2766 87.6278 74.555 87.4176C74.8335 87.2045 75.0451 86.9105 75.19 86.5355C75.3349 86.1577 75.4073 85.7216 75.4073 85.2273C75.4073 84.7386 75.3363 84.3111 75.1942 83.9446C75.0522 83.5753 74.842 83.2884 74.5636 83.0838C74.2852 82.8764 73.9414 82.7727 73.5323 82.7727C73.1062 82.7727 72.7511 82.8821 72.467 83.1009C72.1857 83.3168 71.9741 83.6108 71.832 83.983C71.6928 84.3523 71.6232 84.767 71.6232 85.2273C71.6232 85.6932 71.6942 86.1165 71.8363 86.4972C71.9812 86.875 72.1942 87.1761 72.4755 87.4006C72.7596 87.6222 73.1119 87.733 73.5323 87.733ZM80.3036 88.6534C79.8888 88.6534 79.5124 88.5753 79.1744 88.419C78.8363 88.2599 78.5678 88.0312 78.369 87.733C78.1701 87.4318 78.0707 87.0682 78.0707 86.642C78.0707 86.267 78.1445 85.9631 78.2923 85.7301C78.44 85.4943 78.6374 85.3097 78.8846 85.1761C79.1317 85.0426 79.4045 84.9432 79.7028 84.8778C80.0039 84.8097 80.3065 84.7557 80.6104 84.7159C81.0082 84.6648 81.3306 84.6264 81.5778 84.6009C81.8278 84.5724 82.0096 84.5256 82.1232 84.4602C82.2397 84.3949 82.2979 84.2812 82.2979 84.1193V84.0852C82.2979 83.6648 82.1829 83.3381 81.9528 83.1051C81.7255 82.8722 81.3803 82.7557 80.9173 82.7557C80.4371 82.7557 80.0607 82.8608 79.788 83.071C79.5153 83.2812 79.3235 83.5057 79.2127 83.7443L78.2582 83.4034C78.4286 83.0057 78.6559 82.696 78.94 82.4744C79.2269 82.25 79.5394 82.0937 79.8775 82.0057C80.2184 81.9148 80.5536 81.8693 80.8832 81.8693C81.0934 81.8693 81.3349 81.8949 81.6076 81.946C81.8832 81.9943 82.1488 82.0952 82.4045 82.2486C82.663 82.402 82.8775 82.6335 83.0479 82.9432C83.2184 83.2528 83.3036 83.6676 83.3036 84.1875V88.5H82.2979V87.6136H82.2468C82.1786 87.7557 82.065 87.9077 81.9059 88.0696C81.7468 88.2315 81.5352 88.3693 81.271 88.483C81.0067 88.5966 80.6843 88.6534 80.3036 88.6534ZM80.457 87.75C80.8548 87.75 81.19 87.6719 81.4627 87.5156C81.7383 87.3594 81.9457 87.1577 82.0849 86.9105C82.2269 86.6634 82.2979 86.4034 82.2979 86.1307V85.2102C82.2553 85.2614 82.1616 85.3082 82.0167 85.3509C81.8746 85.3906 81.7099 85.4261 81.5224 85.4574C81.3377 85.4858 81.1573 85.5114 80.9812 85.5341C80.8079 85.554 80.6673 85.571 80.5593 85.5852C80.2979 85.6193 80.0536 85.6747 79.8263 85.7514C79.6019 85.8253 79.4201 85.9375 79.2809 86.0881C79.1445 86.2358 79.0763 86.4375 79.0763 86.6932C79.0763 87.0426 79.2056 87.3068 79.4641 87.4858C79.7255 87.6619 80.0565 87.75 80.457 87.75ZM89.7756 83.4205L88.8722 83.6761C88.8153 83.5256 88.7315 83.3793 88.6207 83.2372C88.5128 83.0923 88.3651 82.973 88.1776 82.8793C87.9901 82.7855 87.75 82.7386 87.4574 82.7386C87.0568 82.7386 86.723 82.831 86.456 83.0156C86.1918 83.1974 86.0597 83.429 86.0597 83.7102C86.0597 83.9602 86.1506 84.1577 86.3324 84.3026C86.5142 84.4474 86.7983 84.5682 87.1847 84.6648L88.1562 84.9034C88.7415 85.0455 89.1776 85.2628 89.4645 85.5554C89.7514 85.8452 89.8949 86.2187 89.8949 86.6761C89.8949 87.0511 89.7869 87.3864 89.571 87.6818C89.358 87.9773 89.0597 88.2102 88.6761 88.3807C88.2926 88.5511 87.8466 88.6364 87.3381 88.6364C86.6705 88.6364 86.1179 88.4915 85.6804 88.2017C85.2429 87.9119 84.9659 87.4886 84.8494 86.9318L85.804 86.6932C85.8949 87.0455 86.0668 87.3097 86.3196 87.4858C86.5753 87.6619 86.9091 87.75 87.321 87.75C87.7898 87.75 88.1619 87.6506 88.4375 87.4517C88.7159 87.25 88.8551 87.0085 88.8551 86.7273C88.8551 86.5 88.7756 86.3097 88.6165 86.1562C88.4574 86 88.2131 85.8835 87.8835 85.8068L86.7926 85.5511C86.1932 85.4091 85.7528 85.1889 85.4716 84.8906C85.1932 84.5895 85.054 84.2131 85.054 83.7614C85.054 83.392 85.1577 83.0653 85.3651 82.7812C85.5753 82.4972 85.8608 82.2741 86.2216 82.1122C86.5852 81.9503 86.9972 81.8693 87.4574 81.8693C88.1051 81.8693 88.6136 82.0114 88.983 82.2955C89.3551 82.5795 89.6193 82.9545 89.7756 83.4205Z"
												fill="#6C7373"
											/>
										</svg>
									)}
								</PieChart>
							</ChartContainer>
						</CardContent>
					</Card>
				</div>

				<ul className="flex w-full items-center gap-8">
					{chartData.map((data) => (
						<li
							key={data.platform}
							className="flex w-full flex-col items-start gap-10 border-b border-solid border-neutral-100 pb-6"
						>
							<div className="flex items-center justify-center gap-2">
								<span
									className="h-2.5 w-2.5 rounded-full"
									style={{ backgroundColor: data.fill }}
								/>

								<p className="font-inter text-P6 font-medium leading-160 text-neutral-500">
									{data.platform}
								</p>
							</div>

							<div className="flex w-full items-center justify-between gap-4">
								<p className="font-inter text-P2 font-medium leading-140 text-neutral-1100">
									{data.sales}
								</p>

								<span className="flex gap-2.5 rounded bg-neutral-100 px-1.5 py-1 font-inter text-P6 font-medium leading-160 text-neutral-900">
									{data.sales === 0
										? "0%"
										: ((data.sales / totalSales) * 100).toFixed(0) + "%"}
								</span>
							</div>
						</li>
					))}
				</ul>
			</div>
		</div>
	);
}
